import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Play } from "lucide-react";
import { cn } from "@/utils/cn";
import { durationLabel } from "../_lib/chat-labels";

/**
 * A video in a media message: its first frame with a play button and the
 * length. Clicking plays it right there, with the browser's controls
 * (fullscreen included).
 */
export const VideoTile = ({ url, className }: { url: string; className?: string }) => {
  const { t } = useTranslation("chat");
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);

  if (playing) {
    return (
      <video
        src={url}
        controls
        autoPlay
        playsInline
        className={cn("block bg-black object-contain", className)}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={t("video.play")}
      className={cn("group relative block overflow-hidden bg-black", className)}
    >
      <video
        // #t=0.1: Safari shows the first frame instead of a black box.
        src={`${url}#t=0.1`}
        muted
        playsInline
        preload="metadata"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) setDuration(d);
        }}
        className="pointer-events-none size-full object-cover"
      />
      <span className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-xs transition-transform group-hover:scale-105">
          <Play className="size-5 translate-x-px fill-current" />
        </span>
      </span>
      {duration != null ? (
        <span className="absolute bottom-1.5 start-2 rounded-full bg-black/55 px-1.5 py-px text-[11px] font-medium tabular-nums text-white">
          {durationLabel(duration * 1000)}
        </span>
      ) : null}
    </button>
  );
};
